import { useState, useMemo } from 'react'
import { Loader2, Plus, RefreshCw, Mail, Users, CheckCircle, XCircle } from 'lucide-react'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  SearchInput,
  PageHeader,
  toast,
} from '@atrvd/saas-ui'
import {
  useInvitations,
  useResendInvitation,
  type InvitationStatus,
} from '@/hooks/useOrganizationInvitations'
import { OrganizationInviteForm } from '@/components/admin/OrganizationInviteForm'
import { InvitationsTable } from '@/components/admin/InvitationsTable'

/**
 * Admin page for managing tenant organization owner invitations
 * Displays invitation statistics, filter controls, and invitation table
 */
export function InvitationsPage() {
  const [statusFilter, setStatusFilter] = useState<InvitationStatus | undefined>(undefined)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [invitationSearch, setInvitationSearch] = useState('')
  const [resendingId, setResendingId] = useState<string | null>(null)

  const { data: invitations, isLoading, error, refetch } = useInvitations(statusFilter)
  const resendInvitation = useResendInvitation()

  /**
   * Filter invitations to only show organization type
   */
  const orgInvitations = useMemo(() => {
    if (!invitations) return []
    return invitations.filter((inv) => inv.invite_type === 'organization')
  }, [invitations])

  /**
   * Filter invitations by search query (organization name or email)
   */
  const filteredInvitations = useMemo(() => {
    if (!invitationSearch || !orgInvitations) return orgInvitations
    const query = invitationSearch.toLowerCase()
    return orgInvitations.filter(
      (inv) =>
        inv.organization_name?.toLowerCase().includes(query) ||
        inv.admin_email.toLowerCase().includes(query)
    )
  }, [orgInvitations, invitationSearch])

  /**
   * Handle resending an invitation.
   * Captures the mutation envelope and surfaces a toast when the invitation
   * record was updated but the email delivery failed (emailSent === false).
   * @param id - Invitation ID to resend
   */
  const handleResend = async (id: string) => {
    setResendingId(id)
    try {
      const result = await resendInvitation.mutateAsync(id)
      // Narrow-cast to read the email-delivery envelope without modifying
      // the shared OrganizationInvitation interface.
      if ((result as { emailSent?: boolean } | null)?.emailSent === false) {
        toast.error('Resent invitation saved, but the email could not be sent. Try again.')
      }
    } catch {
      // Error is handled by mutation
    } finally {
      setResendingId(null)
    }
  }

  /**
   * Calculate invitation statistics by status for organization invitations
   */
  const stats = orgInvitations
    ? {
        total: orgInvitations.length,
        open: orgInvitations.filter((inv) => inv.status === 'open').length,
        accepted: orgInvitations.filter((inv) => inv.status === 'accepted').length,
        expired: orgInvitations.filter((inv) => inv.status === 'expired').length,
      }
    : { total: 0, open: 0, accepted: 0, expired: 0 }

  if (isLoading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Tenant Invitations"
          description="Manage organization owner invitations"
        />
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle>Error loading invitations</CardTitle>
            <CardDescription>{error.message}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => refetch()}>Try Again</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tenant Invitations"
        description="Manage organization owner invitations"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button onClick={() => setIsFormOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create
            </Button>
          </div>
        }
      />

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Invitations</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open</CardTitle>
            <Mail className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.open}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Accepted</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.accepted}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expired</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.expired}</div>
          </CardContent>
        </Card>
      </div>

      {/* Status Filter Buttons */}
      <div className="flex gap-2">
        <Button
          variant={statusFilter === undefined ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter(undefined)}
        >
          All
        </Button>
        <Button
          variant={statusFilter === 'open' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('open')}
        >
          Open
        </Button>
        <Button
          variant={statusFilter === 'accepted' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('accepted')}
        >
          Accepted
        </Button>
        <Button
          variant={statusFilter === 'expired' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('expired')}
        >
          Expired
        </Button>
      </div>

      {/* Search and Invitations Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Invitations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <SearchInput
            placeholder="Search by organization name or email..."
            value={invitationSearch}
            onChange={setInvitationSearch}
            clearable
            className="max-w-sm"
          />
          <InvitationsTable
            invitations={filteredInvitations}
            onResend={handleResend}
            resendingId={resendingId}
          />
        </CardContent>
      </Card>

      {/* Organization Invitation Form Modal */}
      <OrganizationInviteForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        onSuccess={() => {
          refetch()
        }}
      />
    </div>
  )
}